import { ExportOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  getDefaultSortOrder,
  Input,
  Select,
  Spin,
  Table,
  TextField,
} from "@pankod/refine-antd";
import routerProvider from "@pankod/refine-react-router-v6";
import {
  getAccounting,
  getTrackDetailSoldByLabelContributor,
} from "apis/accounting/getApiAccounting";
import { APIhandleAfterPayout } from "apis/tokenValue/getTokenValue";
import { useSelectTime } from "hooks/useSelectTime";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSVLink } from "react-csv";
import styles from "./styles.module.scss";
import { formatNumberWith2DecimalPlaces } from "utils";

export const PayoutsOverviewList = ({ labelId, labelName }: any) => {
  const { Option } = Select;
  const [filter, setFilter] = useState("");
  const timeoutRef = useRef<any>(null);
  const { startTime, endTime, onChangeFilter, timeFramePayoutOptions } =
    useSelectTime();

  const { Link } = routerProvider;

  const [loading, setLoading] = React.useState(Boolean);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingStep2, setLoadingStep2] = useState(false);
  const [loadingStep3, setLoadingStep3] = useState(false);
  const [contributors, setContributors] = useState<any>(null);
  const [contributorsOriginal, setContributorsOriginal] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dataForStep2, setDataForStep2] = useState<any>(null);
  const [dataForStep3, setDataForStep3] = useState<any>(null);
  const [accountingId, setAccountingId] = useState<any>("");
  const [step, setStep] = useState<number>(1);
  const [isLoadingPayout, setLoadingPayout] = useState<any>(false);

  const handleData = useCallback(async () => {
    const data = await getAccounting(
      {
        // search: filter.search,
        startTime: startTime.startTime,
        endTime: endTime.endTime,
        labelId,
      },
      "accounting"
    );
    const customData = data?.data?.data.map((i: any) => ({
      ...i,
      contributorExport: i?.contributor?.username || "",
      metQuotaExport: i?.metQuota ? "YES" : "NO",
      uploadsExport: i?.uploads,
      tracksSoldExport: i?.tracksSold,
      trackEarningsExport: i?.trackEarnings,
      releasesSoldExport: i?.releasesSold,
      releaseEarningsExport: i?.releaseEarnings,
      subscriptionEarningsExport: Number(
        i?.numberSubscriptions * (i?.perDownloadValue || 0)
      ).toFixed(2),
      totalEarningsExport: (
        +i?.trackEarnings +
        +i?.releaseEarnings +
        Number(i?.numberSubscriptions * (i?.perDownloadValue || 0))
      ).toFixed(2),
      processPayoutExport: i?.isAlreadyPayout ? "PROCESSED" : "PROCESS PAYOUT",
    }));
    setContributors(customData || []);
    setContributorsOriginal(customData || []);
  }, [
    endTime.endTime,
    labelId,
    startTime.startTime,
  ]);

  useEffect(() => {
    setLoading(contributors === null);
  }, [contributors]);

  useEffect(() => {
    handleData();
  }, [handleData]);

  const handleChangeSearchStep1 = useCallback(
    (e) => {
      setLoading(true);
      const value = e.target.value;
      setFilter(value);
      if (timeoutRef) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        const filters = contributorsOriginal.filter(
          (i: any) =>
            i?.contributorExport
              ?.toLowerCase()
              .includes(value?.toLowerCase()) ||
            i?.metQuotaExport?.toLowerCase().includes(value.toLowerCase()) ||
            i?.processPayoutExport?.toLowerCase().includes(value.toLowerCase())
        );

        setContributors(filters);
        setLoading(false);
      }, 500);
    },
    [contributorsOriginal]
  );

  const dataTable = useMemo(() => {
    return {
      dataSource: contributors,
      loading: loading,
    };
  }, [contributors, loading]);

  const dataTableForStep2 = useMemo(() => {
    return {
      dataSource: dataForStep2,
      loading: loadingStep2,
    };
  }, [dataForStep2, loadingStep2]);

  const dataTableForStep3 = useMemo(() => {
    return {
      dataSource: dataForStep3,
      loading: loadingStep3,
    };
  }, [dataForStep3, loadingStep3]);

  const handleTimeChange = useCallback(
    (value: any) => {
      setFilter("");
      setLoading(true);
      onChangeFilter("timeFrame", value);
    },
    [onChangeFilter]
  );

  const headers = useMemo(() => {
    if (step === 1) {
      return [
        { label: "Contributor", key: "contributorExport" },
        { label: "Met Quota?", key: "metQuotaExport" },
        { label: "Track Uploads", key: "uploadsExport" },
        { label: "Tracks Sold", key: "tracksSoldExport" },
        { label: "Track Earnings", key: "trackEarningsExport" },
        { label: "Releases Sold", key: "releasesSoldExport" },
        { label: "Release Earnings", key: "releaseEarningsExport" },
        { label: "Subscription Earnings", key: "subscriptionEarningsExport" },
        { label: "Total Earnings($)", key: "totalEarningsExport" },
        { label: "Process Payout", key: "processPayoutExport" },
      ];
    }

    if (step === 2)
      return [
        { label: "Accounting Period", key: "time" },
        { label: "Contributor", key: "contributorName" },
        { label: "Track Published Date", key: "createdAt" },
        { label: "Track Title", key: "title" },
        { label: "Track Artist", key: "artist" },
        { label: "Total Purchases", key: "totalPurchses" },
        { label: "Tokens Earned", key: "tokensEarned" },
        { label: "Current Cash Value($)", key: "currentCashValue" },
      ];

    if (step === 3)
      return [
        { label: "Accounting Period", key: "time" },
        { label: "Purchase Date/Time", key: "createdAt" },
        { label: "User/ Purchaser", key: "purchaser" },
        { label: "Track Title", key: "title" },
        { label: "Track Artist", key: "artist" },
        { label: "Contributor", key: "contributor" },
        { label: "Tokens Cost", key: "tokensCost" },
        // { label: 'Stars Cost', key: 'starsCost' },
      ];
  }, [step]);

  const csvReport = useMemo(() => {
    const data =
      step === 1
        ? dataTable?.dataSource
        : step === 2
        ? dataTableForStep2.dataSource
        : dataTableForStep3.dataSource;
    const filename =
      step === 1
        ? `Accouting_${labelName}_${moment
            .unix(startTime.startTime)
            .format("MM/DD/YYYY")}-${moment
            .unix(endTime.endTime)
            .subtract(1, "days")
            .format("MM/DD/YYYY")}.csv`
        : step === 2
        ? `Accouting_Label_Track_Purchases_Overview_${labelName}_${moment
            .unix(startTime.startTime)
            .format("MM/DD/YYYY")}-${moment
            .unix(endTime.endTime)
            .subtract(1, "days")
            .format("MM/DD/YYYY")}.csv`
        : `Accouting_Label_Track_Purchases_Detail_Overview_${labelName}_${moment
            .unix(startTime.startTime)
            .format("MM/DD/YYYY")}-${moment
            .unix(endTime.endTime)
            .subtract(1, "days")
            .format("MM/DD/YYYY")}.csv`;
    return {
      data: data || [],
      headers: headers,
      filename,
    };
  }, [
    dataTable?.dataSource,
    dataTableForStep2.dataSource,
    dataTableForStep3.dataSource,
    endTime.endTime,
    headers,
    labelName,
    startTime.startTime,
    step,
  ]);

  const handlePayout = async (accountingId: any) => {
    setLoadingPayout(true);
    setAccountingId(accountingId);
    const payload = {
      accountingId,
    };
    const res = await APIhandleAfterPayout(payload);

    const filter = contributors?.map((i: any) => {
      const isAlreadyPayout =
        res?.accounting?._id === i?._id ? true : i?.isAlreadyPayout;
      return {
        ...i,
        isAlreadyPayout,
        contributorExport: i?.contributor?.username || "",
        metQuotaExport: i?.metQuota ? "YES" : "NO",
        uploadsExport: i?.uploads,
        tracksSoldExport: i?.tracksSold,
        trackEarningsExport: i?.trackEarnings,
        releasesSoldExport: i?.releasesSold,
        releaseEarningsExport: i?.releaseEarnings,
        subscriptionEarningsExport: i?.subscriptionEarnings,
        totalEarningsExport:
          i?.trackEarnings + i?.releaseEarnings + i?.subscriptionEarnings,
        processPayoutExport: isAlreadyPayout ? "PROCESSED" : "PROCESS PAYOUT",
      };
    });

    setContributors(filter);
    setContributorsOriginal(filter);
    setLoadingPayout(false);
  };

  // const handleTrackUploads = async (contributor: any) => {
  //   setLoadingStep2(true);
  //   setStep(2);
  //   const payload = {
  //     labelId,
  //     startTime: startTime.startTime,
  //     endTime: endTime.endTime,
  //     userId: contributor?._id,
  //   };
  //   const res = await getTracksUploadedByLabelContributor(payload);
  //   const customData = res?.data.map((i: any) => ({
  //     ...i,
  //     time: `${moment.unix(startTime.startTime).format("MM/DD/YYYY")} - ${moment
  //       .unix(endTime.endTime)
  //       .subtract(1, "days")
  //       .format("MM/DD/YYYY")}`,
  //     contributorName: i?.user.username,
  //     createdAt: `${moment(i.createdAt).format("MM/DD/YYYY")}`,
  //   }));
  //   setDataForStep2(customData || []);
  //   setLoadingStep2(false);
  // };

  // const handleTrackPurchases = async (contributor: any) => {
  //   setLoadingStep2(true);
  //   setStep(2);
  //   const payload = {
  //     labelId,
  //     startTime: startTime.startTime,
  //     endTime: endTime.endTime,
  //     userId: contributor?._id,
  //   };
  //   const res = await getTracksSoldByLabelContributor(payload);
  //   const customData = res?.data.map((i: any) => ({
  //     ...i,
  //     time: `${moment.unix(startTime.startTime).format("MM/DD/YYYY")} - ${moment
  //       .unix(endTime.endTime)
  //       .subtract(1, "days")
  //       .format("MM/DD/YYYY")}`,
  //     contributorName: i?.user.username,
  //     createdAt: `${moment(i.createdAt).format("MM/DD/YYYY")}`,
  //   }));
  //   setDataForStep2(customData || []);
  //   setLoadingStep2(false);
  // };

  const handleTrackPurchaseDetail = async (trackId: any) => {
    setLoadingStep3(true);
    setStep(3);
    const payload = {
      startTime: startTime.startTime,
      endTime: endTime.endTime,
      trackId,
    };
    const res = await getTrackDetailSoldByLabelContributor(payload);
    const customData = res?.data.map((i: any) => ({
      title: i?.track?.title,
      artist: i?.track?.artist,
      contributor: i?.ownerTrack?.username,
      // starsCost: i?.starsCost,
      tokensCost: i?.tokensCost,
      time: `${moment.unix(startTime.startTime).format("MM/DD/YYYY")} - ${moment
        .unix(endTime.endTime)
        .subtract(1, "days")
        .format("MM/DD/YYYY")}`,
      purchaser: i?.user.username,
      createdAt: `${moment(i.createdAt).format("MM/DD/YYYY HH:mm A")}`,
    }));
    setDataForStep3(customData || []);
    setLoadingStep3(false);
  };

  return (
    <div>
      <div className={styles.flex}>
        {step === 1 ? (
          <Form.Item
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Select
              defaultValue={timeFramePayoutOptions[1]}
              style={{
                width: 250,
              }}
              onChange={handleTimeChange}
            >
              {timeFramePayoutOptions.map((province) => (
                <Option key={province.value}>{province.label}</Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <Button
            onClick={() => {
              setStep((pre) => pre - 1);
            }}
          >
            Back
          </Button>
        )}

        <div>
          <CSVLink {...csvReport}>
            <Button disabled={loading || loadingStep2 || loadingStep3}>
              <ExportOutlined />
              Export
            </Button>
            <CSVLink {...csvReport}></CSVLink>
          </CSVLink>
        </div>
      </div>
      {step === 1 ? (
        <div className={styles.OverviewBox}>
          <div
            style={{ marginBottom: "20px", display: "flex", flexWrap: "wrap" }}
          >
            <Input
              onChange={handleChangeSearchStep1}
              value={filter}
              placeholder="Search Contributor, Met Quota?, Process Payout"
            />
          </div>
          <div className={styles.box}>
            <Table
              className={styles.table}
              pagination={false}
              {...dataTable}
              scroll={{ x: true }}
              rowKey="id"
            >
              <Table.Column
                dataIndex="contributor"
                key="contributor"
                title="Contributor"
                sorter={(a: any, b: any) =>
                  a.contributor.username.localeCompare(b.contributor.username)
                }
                render={(value) => (
                  <Link target="_blank" to={`/users/edit/${value?._id}`}>
                    {value.username}
                  </Link>
                )}
                defaultSortOrder={getDefaultSortOrder("contributor")}
              />
              <Table.Column
                dataIndex="metQuota"
                key="metQuota"
                title="Met Quota?"
                render={(value) => <TextField value={value ? "YES" : "NO"} />}
              />
              <Table.Column
                dataIndex="uploads"
                key="uploads"
                title="Track Uploads"
                sorter={(a: any, b: any) => a.uploads - b.uploads}
                render={(_, record: any) => (
                  <TextField
                    // style={{ color: "blue", cursor: "pointer" }}
                    // onClick={() => handleTrackUploads(record?.contributor)}
                    value={record?.uploads}
                  />
                )}
                defaultSortOrder={getDefaultSortOrder("uploads")}
              />
              <Table.Column
                dataIndex="tracksSold"
                key="tracksSold"
                title="Tracks Sold"
                sorter={(a: any, b: any) => a.tracksSold - b.tracksSold}
                render={(_, record: any) => (
                  <TextField
                    // style={{ color: "blue", cursor: "pointer" }}
                    // onClick={() => handleTrackPurchases(record?.contributor)}
                    value={record?.tracksSold}
                  />
                )}
                defaultSortOrder={getDefaultSortOrder("tracksSold")}
              />
              <Table.Column
                dataIndex="trackEarnings"
                key="trackEarnings"
                title="Track Earnings"
                sorter={(a: any, b: any) => a.trackEarnings - b.trackEarnings}
                render={(_, record: any) => (
                  <TextField value={record?.trackEarnings} />
                )}
                defaultSortOrder={getDefaultSortOrder("trackEarnings")}
              />
              <Table.Column
                dataIndex="releasesSold"
                key="releasesSold"
                title="Releases Sold"
                sorter={(a: any, b: any) => a.releasesSold - b.releasesSold}
                render={(_, record: any) => (
                  <TextField value={record?.releasesSold} />
                )}
                defaultSortOrder={getDefaultSortOrder("releasesSold")}
              />
              <Table.Column
                dataIndex="releaseEarnings"
                key="releaseEarnings"
                title="Release Earnings"
                sorter={(a: any, b: any) =>
                  a.releaseEarnings - b.releaseEarnings
                }
                render={(_, record: any) => (
                  <TextField value={record?.releaseEarnings} />
                )}
                defaultSortOrder={getDefaultSortOrder("releaseEarnings")}
              />
              <Table.Column
                dataIndex="subscriptionEarnings"
                key="subscriptionEarnings"
                title="Subscription Earnings"
                sorter={(a: any, b: any) =>
                  a.numberSubscriptions - b.numberSubscriptions
                }
                render={(_, record: any) => (
                  <TextField
                    value={
                      Number(
                        record?.numberSubscriptions *
                          (record?.perDownloadValue || 0)
                      ).toFixed(2) || 0
                    }
                  />
                )}
                defaultSortOrder={getDefaultSortOrder("numberSubscriptions")}
              />
              <Table.Column
                dataIndex=""
                key=""
                title="Total Earnings"
                render={(_, record: any) => (
                  <TextField
                    value={(
                      +record?.trackEarnings +
                      +record?.releaseEarnings +
                      Number(
                        record?.numberSubscriptions *
                          (record?.perDownloadValue || 0)
                      )
                    ).toFixed(2)}
                  />
                )}
              />
              <Table.Column
                dataIndex="metQuota"
                key="metQuota"
                title="Process Payout"
                render={(_, record: any) => (
                  <Button
                    style={{
                      color: record?.isAlreadyPayout ? "white" : "black",
                      minWidth: "165px",
                      fontWeight: "500",
                      backgroundColor: record?.metQuota ? "#52cb10" : "red",
                    }}
                    disabled={isLoadingPayout || record?.isAlreadyPayout}
                    onClick={() => handlePayout(record?._id)}
                  >
                    {isLoadingPayout && accountingId === record?._id ? (
                      <Spin />
                    ) : record?.isAlreadyPayout ? (
                      "PROCESSED"
                    ) : (
                      "PROCESS PAYOUT"
                    )}
                  </Button>
                )}
              />
            </Table>
          </div>
        </div>
      ) : step === 2 ? (
        <div
          style={{
            textAlign: "center",
            marginTop: "10px",
          }}
        >
          <TextField
            value="LABEL TRACK PURCHASES OVERVIEW"
            style={{
              textAlign: "center",
              fontWeight: "700",
              marginBottom: "20px",
              fontSize: "17px",
            }}
          />
          <div
            className={styles.OverviewBox}
            style={{
              marginTop: "10px",
            }}
          >
            <div className={styles.box}>
              <Table
                pagination={false}
                className={styles.table}
                {...dataTableForStep2}
                scroll={{ x: true }}
                rowKey="id"
              >
                <Table.Column
                  dataIndex="time"
                  key="time"
                  title="Accounting Period"
                  render={(value) => <TextField value={value} />}
                />
                <Table.Column
                  dataIndex="user"
                  key="user"
                  title="Contributor"
                  render={(value) => (
                    <Link target="_blank" to={`/users/edit/${value?._id}`}>
                      {value.username}
                    </Link>
                  )}
                  defaultSortOrder={getDefaultSortOrder("user")}
                />
                <Table.Column
                  dataIndex="createdAt"
                  key="createdAt"
                  title="Track Published Date"
                  sorter={(a: any, b: any) =>
                    moment(a.createdAt).diff(moment(b.createdAt))
                  }
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("createdAt")}
                />
                <Table.Column
                  dataIndex="title"
                  key="title"
                  title="Track title"
                  sorter={(a: any, b: any) => a.title.localeCompare(b.title)}
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("title")}
                />
                <Table.Column
                  dataIndex="artist"
                  key="artist"
                  title="Track artist"
                  sorter={(a: any, b: any) => a.artist.localeCompare(b.artist)}
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("artist")}
                />
                <Table.Column
                  dataIndex="totalPurchses"
                  key="totalPurchses"
                  title="Total Purchases"
                  sorter={(a: any, b: any) => a.totalPurchses - b.totalPurchses}
                  render={(_, record: any) => (
                    <TextField
                      style={{ color: "blue", cursor: "pointer" }}
                      onClick={() => handleTrackPurchaseDetail(record?._id)}
                      value={record?.totalPurchses}
                    />
                  )}
                  defaultSortOrder={getDefaultSortOrder("totalPurchses")}
                />
                <Table.Column
                  dataIndex="tokensEarned"
                  key="tokensEarned"
                  title="Tokens Earned"
                  sorter={(a: any, b: any) => a.tokensEarned - b.tokensEarned}
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("tokensEarned")}
                />
                <Table.Column
                  dataIndex="currentCashValue"
                  key="currentCashValue"
                  title="Current Cash Value($)"
                  sorter={(a: any, b: any) =>
                    a.currentCashValue - b.currentCashValue
                  }
                  render={(value) => (
                    <TextField value={formatNumberWith2DecimalPlaces(value)} />
                  )}
                  defaultSortOrder={getDefaultSortOrder("currentCashValue")}
                />
              </Table>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            marginTop: "10px",
          }}
        >
          <TextField
            value="EACH TRACK PURCHASE DETAILS"
            style={{
              textAlign: "center",
              fontWeight: "700",
              marginBottom: "20px",
              fontSize: "17px",
            }}
          />
          <div
            className={styles.OverviewBox}
            style={{
              marginTop: "10px",
            }}
          >
            <div className={styles.box}>
              <Table
                pagination={false}
                className={styles.table}
                {...dataTableForStep3}
                scroll={{ x: true }}
                rowKey="id"
              >
                <Table.Column
                  dataIndex="time"
                  key="time"
                  title="Accounting Period"
                  render={(value) => <TextField value={value} />}
                />
                <Table.Column
                  dataIndex="createdAt"
                  key="createdAt"
                  title="Purchase Date/Time"
                  sorter={(a: any, b: any) =>
                    moment(a.createdAt).diff(moment(b.createdAt))
                  }
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("createdAt")}
                />
                <Table.Column
                  dataIndex="purchaser"
                  key="purchaser"
                  title="User/ Purchaser"
                  sorter={(a: any, b: any) =>
                    a.purchaser.localeCompare(b.purchaser)
                  }
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("purchaser")}
                />
                <Table.Column
                  dataIndex="title"
                  key="title"
                  title="Track title"
                  sorter={(a: any, b: any) => a.title.localeCompare(b.title)}
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("title")}
                />
                <Table.Column
                  dataIndex="artist"
                  key="artist"
                  title="Track artist"
                  sorter={(a: any, b: any) => a.artist.localeCompare(b.artist)}
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("artist")}
                />
                <Table.Column
                  dataIndex="contributor"
                  key="contributor"
                  title="Contributor"
                  sorter={(a: any, b: any) =>
                    a.contributor.localeCompare(b.contributor)
                  }
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("contributor")}
                />
                <Table.Column
                  dataIndex="tokensCost"
                  key="tokensCost"
                  title="Tokens cost"
                  sorter={(a: any, b: any) => a.tokensCost - b.tokensCost}
                  render={(value) => <TextField value={value} />}
                  defaultSortOrder={getDefaultSortOrder("tokensCost")}
                />
              </Table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
